import React, { useCallback, useMemo, useState } from "react"

import { toUpdateRunTags } from "@/runs/values"
import { TagEditor, useOrganizationTags } from "@/tags"
import { normalizeTags } from "@/tags/values"

import { logger } from "@/lib/logger"

import { useConversationUpdateRunTagsTransport } from "../routes/useConversationRunTagsTransport"
import { useInvalidateConversationsResultsTransport } from "../routes/useConversationsResultsTransportInvalidation"

const log = logger("ConversationTagsCell")

// Match the tag presentation used by the simulations table (RunTagsCell):
// 0 visible named tags so every tag collapses into a color-dot chip (colors
// only, never label text), with an add button on the right.
const MAX_VISIBLE_TAGS = 0
const MAX_OVERFLOW_DOTS = 3

interface ConversationTagsCellProps {
  runId: string
  initialTags?: string[]
}

export const ConversationTagsCell = React.memo<ConversationTagsCellProps>(
  ({ runId, initialTags = [] }) => {
    const [optimisticTags, setOptimisticTags] = useState<string[] | null>(null)
    const { suggestions, invalidate: invalidateOrgTags } = useOrganizationTags()
    const updateTagsMutation = useConversationUpdateRunTagsTransport()
    const invalidateConversationsResultsTransport =
      useInvalidateConversationsResultsTransport()

    const currentTags = useMemo(() => {
      if (!optimisticTags) {
        return initialTags
      }
      const initialSet = new Set(initialTags)
      const isSame =
        initialSet.size === optimisticTags.length &&
        optimisticTags.every((t) => initialSet.has(t))
      return isSame ? initialTags : optimisticTags
    }, [initialTags, optimisticTags])

    const handleTagsChange = useCallback(
      (tags: string[]) => {
        const nextTags = normalizeTags(tags)
        setOptimisticTags(nextTags)
        updateTagsMutation.mutate(
          { run_id: runId, tags: toUpdateRunTags(nextTags) },
          {
            onError: (error: unknown) => {
              setOptimisticTags(null)
              log.warn("Failed to update tags", {
                run_id: runId,
                error: error instanceof Error ? error.message : String(error),
              })
            },
            onSettled: () => {
              void invalidateOrgTags()
              void invalidateConversationsResultsTransport()
            },
          },
        )
      },
      [
        runId,
        updateTagsMutation,
        invalidateOrgTags,
        invalidateConversationsResultsTransport,
      ],
    )

    return (
      <TagEditor
        tags={currentTags}
        onTagsChange={handleTagsChange}
        suggestions={suggestions}
        showInput
        allowRemove
        allowColorEdit
        allowNameEdit
        wrap={false}
        collapseOverflow
        maxVisibleTags={MAX_VISIBLE_TAGS}
        maxOverflowDots={MAX_OVERFLOW_DOTS}
        className="w-full justify-end"
      />
    )
  },
)
ConversationTagsCell.displayName = "ConversationTagsCell"
